import { readPublicEnvironment } from './environment';

const apiBaseUrl = (readPublicEnvironment('VITE_API_BASE_URL') || 'http://localhost:8000/api/v1').replace(/\/$/, '');

interface ConsentPolicy {
  version: string;
  url: string;
  required: boolean;
}

export interface ConsentPolicyConfiguration {
  terms: ConsentPolicy;
  privacy: ConsentPolicy;
  marketing: ConsentPolicy;
}

export interface ContactLeadPayload {
  submission_id: string;
  full_name: string;
  work_email: string;
  organisation_name: string;
  phone: string;
  organisation_type: string;
  enquiry_type: string;
  message: string;
  privacy_accepted: boolean;
  privacy_version: string;
  marketing_consent: boolean;
  marketing_version: string;
  turnstile_token: string;
  website: string;
}

export class ContactApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details: unknown,
  ) {
    super(message);
    this.name = 'ContactApiError';
  }
}

async function parseResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) return response.json() as Promise<unknown>;
  return response.text();
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body) headers.set('Content-Type', 'application/json');

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, { ...options, headers });
  } catch (cause) {
    throw new ContactApiError('The enquiry service could not be reached.', 0, cause);
  }
  const body = await parseResponse(response);
  if (!response.ok) {
    const message = typeof body === 'object' && body && 'detail' in body && typeof body.detail === 'string'
      ? body.detail
      : `Request failed with status ${response.status}`;
    throw new ContactApiError(message, response.status, body);
  }
  return body as T;
}

export function loadContactPolicies(): Promise<ConsentPolicyConfiguration> {
  return request<ConsentPolicyConfiguration>('/auth/consents/config/');
}

export function submitContactLead(payload: ContactLeadPayload): Promise<{ detail: string }> {
  return request<{ detail: string }>('/public/contact-leads/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function contactErrorMessage(error: unknown): string {
  if (!(error instanceof ContactApiError)) {
    return 'Your enquiry could not be sent. Please try again.';
  }
  if (error.status === 429) return 'Too many enquiries were submitted. Please wait and try again.';
  if (error.status === 503 || error.status === 0) {
    return 'The enquiry service is temporarily unavailable. Please try again later.';
  }
  if (error.details && typeof error.details === 'object') {
    const details = error.details as Record<string, unknown>;
    const messages: string[] = [];
    const collect = (value: unknown) => {
      if (typeof value === 'string') messages.push(value);
      else if (Array.isArray(value)) value.forEach(collect);
      else if (value && typeof value === 'object') Object.values(value).forEach(collect);
    };
    collect(details.errors ?? details);
    if (messages[0]) return messages[0];
  }
  return error.message || 'Your enquiry could not be sent. Please try again.';
}
